import React, { useCallback } from 'react';
import { useSelector } from 'react-redux';
import { UIRootState } from 'ui/store/reducers';
import { NewGameHeader } from './components/NewGameHeader';
import { NewGameTabPanel } from './components/NewGameTabPanel';
import { translate } from 'game/translate';
import { Store } from 'game/store/gameStore';
import { SolitaireGame } from 'game/SolitaireGame';

export const NewGameModal: React.FC<{ gameUiRef?: React.RefObject<HTMLDivElement> }> = ({ gameUiRef }) => {
  const isOpen = useSelector((state: UIRootState) => state.gameUI.modals.newGameModal?.isOpen);
  const currentDifficulty = useSelector((state: UIRootState) => state.gameUI.modals.newGameModal?.currentDifficulty);

  const [panelVisible, setPanelVisible] = React.useState<boolean>(false);
  const [panelClosing, setPanelClosing] = React.useState<boolean>(false);
  const ref = React.useRef<HTMLDivElement>(null);
  const modalRef = React.useRef<HTMLDivElement>(null);

  React.useEffect(() => {
    if (isOpen) {
      setPanelVisible(true);
      setPanelClosing(true);
      const t = setTimeout(() => setPanelClosing(false), 0);
      return () => clearTimeout(t);
    } else if (panelVisible) {
      setPanelClosing(true);
      const t = setTimeout(() => {
        setPanelVisible(false);
        setPanelClosing(false);
      }, 360);
      return () => clearTimeout(t);
    }
  }, [isOpen]);

  const handleButtonClick = (buttonKey: string, data?: any) => {
    document.dispatchEvent(new CustomEvent('ui-button-click', {
      detail: { buttonKey, data }
    }));
  };

  const handlePlayBtnClick = useCallback(() => {
    if (currentDifficulty?.dataDraw || currentDifficulty?.dataSuit) {
        if (currentDifficulty.dataDraw) {
            document.getElementById('game-container')?.setAttribute('data-draw', currentDifficulty.dataDraw);
        }

        if (currentDifficulty.dataSuit) {
            document.getElementById('game-container')?.setAttribute('data-suit', currentDifficulty.dataSuit);
        }
    }

    // Don't save to localStorage during tutorial
    if (!Store.tutorialActive) {
        if (currentDifficulty?.dataSuit && Store.name) {
            localStorage.setItem(`${Store.name}_last_suits`, currentDifficulty.dataSuit);
        }

        if (currentDifficulty?.dataDraw && Store.name) {
            localStorage.setItem(`${Store.name}_last_draw`, currentDifficulty.dataDraw);
        }
    }

    Store.reInit();

    SolitaireGame.instance?.resetGame();
    
    if (currentDifficulty?.type) {
        setTimeout(() => {
            dispatchEvent(
                new CustomEvent("newGame", {
                    detail: {
                        difficulty: currentDifficulty.type,
                        balanced: currentDifficulty.type.includes('_BALANCED'),
                        suits: currentDifficulty.dataSuit,
                        name: Store.name,
                        draw: currentDifficulty.dataDraw,
                    },
                })
            );
        }, 50);

        handleButtonClick('close-new-game');
    }
  }, [currentDifficulty, handleButtonClick]);

  const handleRestartBtnClick = useCallback(() => {
    dispatchEvent(new CustomEvent("restartGame"));
    handleButtonClick('close-new-game');
  }, []);

  if (!panelVisible) return null;

  return (
    <div id="game-ui-new-game" ref={modalRef} className={`${Store.name === 'Spider' && Store.language === 'gr' ? 'new-game-small-tab-text ' : ''}design-modal text-white flex-column h-100 w-100 panel--open ${panelClosing ? 'panel--closing' : ''}`}>
      <NewGameHeader />
      <NewGameTabPanel footerHeight={ref.current?.clientHeight || 0} modalRef={modalRef} gameUiRef={gameUiRef} />
      <div ref={ref} className="d-flex flex-column" style={{ gap: '12px' }}>
            <div className="panel-divider"/>
            <span className="new-game-warning-text">{translate('new_game_warning_text')}</span>

            <div className="custom-new-game-button custom-new-game-button--primary" onClick={handlePlayBtnClick}>
              <span className='d-flex align-items-center justify-content-center align-self-center' style={{ height: '-webkit-fill-available' }}>
                {translate('new_game_play')}
              </span>
            </div>

            <div className="custom-new-game-button custom-new-game-button--secondary" onClick={handleRestartBtnClick}>
              <span className='d-flex align-items-center justify-content-center align-self-center' style={{ height: '-webkit-fill-available' }}>
                {translate('restart_game_button')}
              </span>
            </div>
        </div>
    </div>
  );
};


